<template>
  <highcharts ref="chart" :options="options" />
</template>

<script>
import '@/plugins/highcharts.js'
import Modules from '@/models/Modules.js'
import Travel from '@/models/carbon/Travel.js'
import Commute from '@/models/carbon/Commute.js'

export default {
  name: 'GHGITreeMap',
  props: {
    ghgi: {
      type: Object,
      default: null
    },
    modules: {
      type: Array,
      default: function () {
        return []
      }
    },
    colors: {
      type: Object
    },
    enabledLabels: {
      type: Boolean,
      default: true
    },
    height: {
      type: Number,
      default: 400
    },
    exporting: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    series: function () {
      let series = []
      for (let module of this.modules) {
        series.push({
          id: module,
          name: this.$t(module),
          color: this.colors[module],
          value: this.ghgi.carbonSynthesis[module].intensity
        })
      }
      if (this.modules.includes(Modules.HEATINGS)) {
        for (let building of this.ghgi.buildings) {
          for (let heating of building.heatings) {
            let oindex = series.findIndex(
              (obj) =>
                obj.parent === Modules.HEATINGS &&
                obj.name === this.$t(heating.type)
            )
            let cheatingIntensity = building.getHeatingCarbonIntensity(
              heating,
              this.ghgi.year
            )
            if (oindex < 0) {
              series.push({
                parent: Modules.HEATINGS,
                name: this.$t(heating.type),
                value: cheatingIntensity.intensity
              })
            } else {
              series[oindex].value += cheatingIntensity.intensity
            }
          }
        }
      }
      if (this.modules.includes(Modules.PURCHASES)) {
        for (let category of Object.keys(
          this.ghgi.emissions.purchases.categories
        )) {
          series.push({
            parent: Modules.PURCHASES,
            name: this.$t(category),
            value:
              this.ghgi.emissions.purchases.categories[category].total.intensity
          })
        }
      }
      if (this.modules.includes(Modules.DEVICES)) {
        for (let device of this.ghgi.devices) {
          let oindex = series.findIndex(
            (obj) =>
              obj.parent === Modules.DEVICES &&
              obj.name === this.$t(device.type)
          )
          if (oindex < 0) {
            series.push({
              parent: Modules.DEVICES,
              name: this.$t(device.type),
              value: device.intensity.intensity
            })
          } else {
            series[oindex].value += device.intensity.intensity
          }
        }
      }
      if (this.modules.includes(Modules.TRAVELS)) {
        let index = 0
        for (let transportation of Travel.transportations) {
          series.push({
            parent: Modules.TRAVELS,
            name: this.$t(transportation),
            value:
              this.ghgi.emissions.travels.transportations.intensity[index]
                .intensity
          })
          index += 1
        }
      }
      if (this.modules.includes(Modules.VEHICLES)) {
        for (let vehicle of this.ghgi.vehicles) {
          let oindex = series.findIndex(
            (obj) =>
              obj.parent === Modules.VEHICLES &&
              obj.name === this.$t(vehicle.type)
          )
          if (oindex < 0) {
            series.push({
              parent: Modules.VEHICLES,
              name: this.$t(vehicle.type),
              value: vehicle.intensity.intensity
            })
          } else {
            series[oindex].value += vehicle.intensity.intensity
          }
        }
      }
      if (this.modules.includes(Modules.FOODS)) {
        let intensities = this.ghgi.emissions.foods.meals.intensity
        for (let meal of Object.keys(intensities)) {
          let oindex = series.findIndex(
            (obj) => obj.parent === Modules.FOODS && obj.name === this.$t(meal)
          )
          if (oindex < 0) {
            series.push({
              parent: Modules.FOODS,
              name: this.$t(meal),
              value: intensities[meal].intensity
            })
          } else {
            series[oindex].value += intensities[meal].intensity
          }
        }
      }
      if (this.modules.includes(Modules.COMMUTES)) {
        for (let mode of Commute.TRAVELS_MODES) {
          series.push({
            parent: Modules.COMMUTES,
            name: this.$t(mode),
            value: 0
          })
        }
        let intensity = this.ghgi.emissions.commutes.status.intensity
        for (let status of Object.keys(intensity)) {
          let index = 0
          for (let mintensity of intensity[status]) {
            let oindex = series.findIndex(
              (obj) =>
                obj.parent === Modules.COMMUTES &&
                obj.name === this.$t(Commute.TRAVELS_MODES[index])
            )
            series[oindex].value += mintensity.intensity
            index += 1
          }
        }
      }
      if (this.modules.includes(Modules.RACTIVITIES)) {
        for (let ractivity of this.ghgi.ractivities) {
          let oindex = series.findIndex(
            (obj) =>
              obj.parent === Modules.RACTIVITIES &&
              obj.name === this.$t(ractivity.type)
          )
          if (oindex < 0) {
            series.push({
              parent: Modules.RACTIVITIES,
              name: this.$t(ractivity.type),
              value: ractivity.intensity.intensity
            })
          } else {
            series[oindex].value += ractivity.intensity.intensity
          }
        }
      }
      return series
    },
    options: function () {
      const self = this
      return {
        chart: {
          height: this.height
        },
        title: {
          text: ''
        },
        exporting: {
          enabled: this.exporting
        },
        series: [
          {
            type: 'treemap',
            layoutAlgorithm: 'stripes',
            alternateStartingDirection: true,
            borderWidth: 2,
            dataLabels: {
              style: {
                textOutline: 'none'
              }
            },
            levels: [
              {
                level: 1,
                layoutAlgorithm: 'sliceAndDice',
                dataLabels: {
                  enabled: true,
                  align: 'left',
                  verticalAlign: 'top'
                }
              }
            ],
            data: this.series
          }
        ],
        legend: {
          enabled: false
        },
        tooltip: {
          formatter: function () {
            return `${this.key}<br/><b>${self.$options.filters.splitNumberTons(
              this.point.value
            )} ${self.$t('t.co2e')}</b>`
          }
        }
      }
    }
  }
}
</script>
